import type { ChatwootAssignee, ChatwootTeamRef } from './types'

// Operators assign to and mention Lohnbuchhaltung far more than anyone else on
// the account, so those teammates lead the pickers and everyone else follows.
// Matched case-insensitively against the Chatwoot team names; if no
// such team exists on the account, every picker simply stays alphabetical. Kept
// as a list so a second team can be promoted without reworking the comparators —
// only add one Ops has actually asked for.
const PRIORITY_TEAM_NAMES = ['lohnbuchhaltung']

// Alphabetical within a relevance group. German collation so umlauts sort where
// operators expect them ("Ötztal" next to "Ostsee", not after "Z").
const collator = new Intl.Collator('de', { sensitivity: 'base', numeric: true })

// The label a picker shows for an agent — also what it sorts by, so the order on
// screen matches the text on screen.
export function agentLabel(agent: ChatwootAssignee): string {
  return agent.name || agent.email || agent.id
}

export function teamLabel(team: ChatwootTeamRef): string {
  return team.name || team.id
}

function isPriorityTeam(team: ChatwootTeamRef): boolean {
  return PRIORITY_TEAM_NAMES.includes((team.name || '').trim().toLowerCase())
}

// The ids of the teams whose members lead the pickers.
export function priorityTeamIds(teams: ChatwootTeamRef[]): Set<string> {
  return new Set(teams.filter(isPriorityTeam).map((team) => team.id))
}

// Agents ranked by relevance: the caller first when `selfAgentId` is given, then
// members of a priority team, then everyone else, alphabetically within each
// group. Pure — returns a new array.
//
// `teamIds` comes from chatwoot/get_directory and is absent when membership
// couldn't be read, which degrades to a plain alphabetical list rather than an
// arbitrary one.
//
// `selfAgentId` is opt-in per picker rather than baked in, because leading with
// yourself only makes sense where you are a plausible choice. Assigning a ticket
// to yourself is the commonest action there is — Intercom put you at the top and
// operators late in the alphabet felt the loss — whereas @-mentioning
// yourself is not something anyone does, so the mention picker passes nothing.
export function sortAgentsByRelevance(
  agents: ChatwootAssignee[],
  teams: ChatwootTeamRef[],
  selfAgentId?: string
): ChatwootAssignee[] {
  const priority = priorityTeamIds(teams)
  const rank = (agent: ChatwootAssignee) => {
    if (selfAgentId && agent.id === selfAgentId) {
      return -1
    }
    return agent.teamIds?.some((id) => priority.has(id)) ? 0 : 1
  }
  return [...agents].sort(
    (a, b) =>
      rank(a) - rank(b) || collator.compare(agentLabel(a), agentLabel(b))
  )
}

// Teams ranked the same way, so the priority team heads its own group too.
export function sortTeamsByRelevance(
  teams: ChatwootTeamRef[]
): ChatwootTeamRef[] {
  const rank = (team: ChatwootTeamRef) => (isPriorityTeam(team) ? 0 : 1)
  return [...teams].sort(
    (a, b) => rank(a) - rank(b) || collator.compare(teamLabel(a), teamLabel(b))
  )
}
